import * as React from 'react'
import { ChevronRightIcon } from '@heroicons/react/24/outline'

interface JsonNodeProps {
  label: string
  value: unknown
  depth: number
  defaultExpanded?: boolean
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)
const isArray = (v: unknown): v is unknown[] => Array.isArray(v)

const JsonNode: React.FC<JsonNodeProps> = ({ label, value, depth, defaultExpanded }) => {
  const [expanded, setExpanded] = React.useState(!!defaultExpanded)
  const expandable = isObject(value) || isArray(value)
  const isRoot = depth === 0

  if (!expandable) {
    return (
      <div className="flex gap-1.5 leading-6" style={{ paddingInlineStart: depth * 14 }} dir="ltr">
        <span className="text-accent">{isRoot ? label : <>&quot;{label}&quot;</>}</span>
        <span className="text-muted-foreground">:</span>
        <span className="text-foreground break-all">{JSON.stringify(value)}</span>
      </div>
    )
  }

  const entries = isArray(value)
    ? value.map((v, i) => [String(i), v] as const)
    : Object.entries(value as Record<string, unknown>)

  return (
    <div dir="ltr">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className="flex items-center gap-1 leading-6 hover:text-foreground"
        style={{ paddingInlineStart: depth * 14 }}
      >
        <ChevronRightIcon
          aria-hidden="true"
          className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
        />
        <span className="text-accent">{isRoot ? label : <>&quot;{label}&quot;</>}</span>
        <span className="text-muted-foreground">{isArray(value) ? `[${entries.length}]` : `{${entries.length}}`}</span>
      </button>
      {expanded &&
        entries.map(([k, v]) => (
          <JsonNode key={k} label={k} value={v} depth={depth + 1} />
        ))}
    </div>
  )
}

export interface JsonTreeProps {
  data: unknown
  rootLabel: string
  defaultExpanded?: boolean
  className?: string
}

export const JsonTree: React.FC<JsonTreeProps> = ({ data, rootLabel, defaultExpanded = false, className }) => (
  <div className={`font-mono text-xs ${className ?? ''}`}>
    <JsonNode label={rootLabel} value={data} depth={0} defaultExpanded={defaultExpanded} />
  </div>
)
